/**
 * One document-wide answer to "is a Turbo render in progress?".
 *
 * A render removes focused, edited controls mid-swap, and the browser fires `change` on them as a
 * side effect. Controllers that must not treat that as a person's action ask here instead of each
 * tracking the render lifecycle themselves.
 *
 * Counted, not a flag: a page render and a frame render can overlap.
 */

let renderDepth = 0;

export function isTurboRenderInProgress(): boolean {
   return renderDepth > 0;
}

function onRenderStart(): void {
   renderDepth++;
}

function onRenderEnd(): void {
   renderDepth = Math.max(0, renderDepth - 1);
}

// A cancelled or aborted render never reaches onRenderEnd; load marks every render as over.
function onRenderSettled(): void {
   renderDepth = 0;
}

/** Installs the document listeners. Returns an uninstall function (tests). */
export function installTurboRenderTracker(): () => void {
   document.addEventListener("turbo:before-render", onRenderStart);
   document.addEventListener("turbo:before-frame-render", onRenderStart);
   document.addEventListener("turbo:render", onRenderEnd);
   document.addEventListener("turbo:frame-render", onRenderEnd);
   document.addEventListener("turbo:load", onRenderSettled);
   document.addEventListener("turbo:frame-load", onRenderSettled);

   return () => {
      document.removeEventListener("turbo:before-render", onRenderStart);
      document.removeEventListener("turbo:before-frame-render", onRenderStart);
      document.removeEventListener("turbo:render", onRenderEnd);
      document.removeEventListener("turbo:frame-render", onRenderEnd);
      document.removeEventListener("turbo:load", onRenderSettled);
      document.removeEventListener("turbo:frame-load", onRenderSettled);
      renderDepth = 0;
   };
}
